import { useCallback, useMemo, useRef, useState, useId, useContext, useEffect } from 'react';
import { AuthContext } from '../../authContext';
import { openOneDrivePicker } from '../OneDriveFileButton/helper';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import FileButton from '../FileButton';
import OneDriveOptions from '../OneDriveOptions/OneDriveOptions';
import OneDriveFilesList from '../OneDriveFilesList/OneDriveFilesList';
import './FileUpload.css';

function FileUpload({ onFilesSelected, uploadingFiles = [], documentList = [], onDeleteDocument, isUploading, acceptedTypes = ['.pdf', '.docx', '.xlsx'], showCloseIcon = false, onClose, multiple = true, assistantId, onFolderSelected, disabled = false, canCreateFolder = true
}) {

  const { t } = useTranslation('assistantConfiguration');
  const [isDragging, setIsDragging] = useState(false);
  const [showOneDriveOptions, setShowOneDriveOptions] = useState(false);
  const [showOneDriveFilesList, setShowOneDriveFilesList] = useState(false);
  const [oneDriveFiles, setOneDriveFiles] = useState([]);
  const [oneDriveFolders, setOneDriveFolders] = useState([]);
  const fileInputRef = useRef(null);
  const inputId = useId();
  const { accessToken } = useContext(AuthContext);
  console.log('uploadingFiles', uploadingFiles)
  console.log('documentList', documentList)

  const acceptAttribute = useMemo(() => acceptedTypes.join(','), [acceptedTypes]);

  const handleFiles = useCallback((fileList) => {
    if (!fileList) {
      return;
    }

    const files = Array.from(fileList);
    if (files.length === 0) {
      return;
    }

    onFilesSelected?.(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onFilesSelected]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    // Evita alternância indevida quando arrasta sobre elementos filhos
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    handleFiles(event.dataTransfer?.files);
  }, [handleFiles]);

  const fetchOneDriveFolders = useCallback(async () => {
    if (!assistantId) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/get_onedrive_folders_selfassistant?assistant_id=${assistantId}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        const fetchedFolders = data.onedrive_folders || [];
        const fetchedFiles = data.documents || [];
        setOneDriveFolders(fetchedFolders);
        setOneDriveFiles(fetchedFiles);
        if (fetchedFolders.length > 0 || fetchedFiles.length > 0) {
          setShowOneDriveFilesList(true);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar pastas:", error);
    }
  }, [assistantId, accessToken]);

  useEffect(() => {
    fetchOneDriveFolders();
  }, [fetchOneDriveFolders]);

  const handleOneDriveAddFile = async () => {
    try {
      const result = await openOneDrivePicker('files', { filters: acceptedTypes });
      if (!result) return;

      const items = Array.isArray(result) ? result : (result.items || result.data || []);
      if (items.length === 0) return;

      const optimisticFiles = items.map(item => ({
        filename: item.name,
        synced_at: new Date().toISOString(),
        id: item.id,
        _optimistic: true,
      }));
      setOneDriveFiles(prev => [...prev, ...optimisticFiles]);
      setShowOneDriveOptions(false);
      setShowOneDriveFilesList(true);

      for (const item of items) {
        (async () => {
          try {
            const res = await fetch(item['@content.downloadUrl']);
            const blob = await res.blob();
            const file = new File([blob], item.name, { type: blob.type });

            const formData = new FormData();
            formData.append('assistant_id', assistantId);
            formData.append('files', file);
            formData.append('onedrive_file_id', item.id);
            formData.append('onedrive_file_name', item.name);

            await fetch(`${process.env.REACT_APP_API_URL}/post_async_documents_selfassistant`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${accessToken}` },
              body: formData
            });
          } catch (err) {
            console.error('Erro ao enviar arquivo ao back:', err);
          }
        })();
      }
    } catch (error) {
      console.error('Erro ao adicionar arquivo do OneDrive:', error);
    }
  };

  const handleOpenOptions = () => {
    setShowOneDriveFilesList(false); 
    setShowOneDriveOptions(true);    
  };

  const handleOneDriveAddFolder = async () => {
    try {
      const result = await openOneDrivePicker('folder');

      if (result) {
        const folderName = result.name || result.folder_name || (Array.isArray(result) ? result[0]?.name : null) || 'Nova pasta';
        const optimisticFolder = {
          folder_name: folderName,
          folder_id: result.id || result.folder_id || `optimistic-${Date.now()}`,
          drive_id: result.drive_id || '',
          user_email: result.user_email || '',
          synced_at: new Date().toISOString(),
          _optimistic: true,
        };
        setOneDriveFolders(prev => [...prev, optimisticFolder]);
        setShowOneDriveOptions(false);
        setShowOneDriveFilesList(true);

        if (onFolderSelected) {
          onFolderSelected(result).catch(error => console.error('Erro ao enviar pasta ao back:', error));
        }
      }
    } catch (error) {
      console.error("Erro ao selecionar pasta do OneDrive:", error);
    }
  };

  const handleCloseOneDriveOptions = () => {
    setShowOneDriveOptions(false);

    if (oneDriveFolders.length > 0) {
      setShowOneDriveFilesList(true);
    }
  };

  const handleCloseOneDriveFilesList = () => {
    setShowOneDriveFilesList(false);
    setOneDriveFiles([]);
    setOneDriveFolders([]);
  };

  const oneDriveFileIds = useMemo(
    () => new Set(oneDriveFiles.map(f => f.id).filter(Boolean)),
    [oneDriveFiles]
  );

  const localDocumentList = useMemo(
    () => documentList.filter(doc => !doc.onedrive_file_id && !oneDriveFileIds.has(doc.id)),
    [documentList, oneDriveFileIds]
  );

  const hasFileList = uploadingFiles.length > 0 || localDocumentList.length > 0;
  const storagePercentage = 76; // Hardcoded for now

  console.log('isUploading', isUploading)
  console.log('hasFileList', hasFileList)

  return (
    <div className={`upload-container ${hasFileList ? 'has-file-list' : ''}`}>
      {showCloseIcon && <img src="/imagens/x-black.svg" alt="Close" className="close-icon" onClick={onClose} />}
      <div className="upload-wrapper">
        <p>{t('ai_files')}</p>
        {canCreateFolder && <img src="/imagens/info.svg" alt="Info icon" className="info-icon" title={t('add_files_tooltip')} />}
      </div>
      <div className="upload-containers-wrapper">
        <div
          className={`upload-container-inner ${disabled ? 'disabled' : ''}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <button 
            className="upload-content"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            >
            <img src="/imagens/upload-icon.svg" alt="Icon upload" className="upload-icon" />
            <span className="upload-text">
              <span className="upload-row">
                {t('dragDropText')} <label htmlFor={inputId} className="upload-link file-upload-label" onClick={(e) => e.stopPropagation()}>{t('chooseFileFromDevice')}</label>
              </span>
              <input
                id={inputId}
                ref={fileInputRef}
                type="file"
                className="file-upload-input"
                accept={acceptAttribute}
                multiple={multiple}
                disabled={disabled}
                onChange={(event) => {if (!disabled) { handleFiles(event.target.files); }}}
              />
            </span>
          </button>
          {(uploadingFiles.length > 0 || localDocumentList.length > 0) && (
          <div className="upload-file-list">

            {uploadingFiles.map((file, index) => (
              <div className="uploading-file-item" key={`uploading-${file.filename}-${index}`}>
                {isUploading && <CircularProgress size={16} />}
                <span className="uploading-file-name">{file.filename}</span>
              </div>
            ))}

            {localDocumentList.map((doc, index) => (
              <FileButton
                key={`${doc.filename}-${index}`}
                filename={doc.filename}
                onDelete={() => onDeleteDocument?.(doc)}
              />
            ))}
          </div>
        )}
        </div>

        <div 
          className={`upload-container-onedrive ${disabled ? 'disabled' : ''}`} 
          disabled={disabled}
          onChange={(event) => {if (!disabled) { handleFiles(event.target.files); }}}>
          {!showOneDriveOptions && !showOneDriveFilesList && (
            <>
              <button
                className="btn-onedrive-self"
                onClick={() => {
                  if (canCreateFolder) {
                    setShowOneDriveOptions(true);
                  } else {
                    handleOneDriveAddFile();
                  }
                }}
                disabled={disabled}
              >
                <img
                  src="/imagens/onedrive.svg"
                  alt="onedrive-icon"
                  className="img-onedrive-self"
                />
                {t('oneDriveButton')}
              </button>
              {canCreateFolder && <p className="text-onedrive-self">{t('oneDriveText')}</p>}
            </>
          )}
          {showOneDriveOptions && !showOneDriveFilesList && (
            <OneDriveOptions
              onAddFile={handleOneDriveAddFile}
              onAddFolder={handleOneDriveAddFolder}
              onClose={handleCloseOneDriveOptions} 
              canCreateFolder={canCreateFolder}
            />
          )}
          {showOneDriveFilesList && (
            <OneDriveFilesList
              files={oneDriveFiles}
              folders={oneDriveFolders}
              onClose={handleCloseOneDriveFilesList}
              onOpenOptions={handleOpenOptions}
              assistantId={assistantId}
              setFiles={setOneDriveFiles}
              setFolders={setOneDriveFolders}
              canCreateFolder={canCreateFolder}
              onAddFile={handleOneDriveAddFile}
            />
          )}
        </div>
      </div>
      {/* <div className="storage-info">
        <p className="storage-title">{t('storage')}</p>
        <div className="storage-bar-container">
          <div className="storage-bar-background">
            <div 
              className="storage-bar-fill" 
              style={{ width: `${storagePercentage}%` }}
            ></div>
          </div>
          <span className="storage-percentage">{storagePercentage}%</span>
        </div>
      </div> */}
    </div>
  );
}

export default FileUpload;